import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TONE_MAP = {
  cyan: { bg: 'rgba(34,211,238,0.10)', bgHover: 'rgba(34,211,238,0.18)', text: '#67e8f9', border: 'rgba(103,232,249,0.30)', glow: 'rgba(34,211,238,0.25)' },
  violet: { bg: 'rgba(139,92,246,0.10)', bgHover: 'rgba(139,92,246,0.18)', text: '#c4b5fd', border: 'rgba(196,181,253,0.30)', glow: 'rgba(139,92,246,0.25)' },
  emerald: { bg: 'rgba(16,185,129,0.10)', bgHover: 'rgba(16,185,129,0.18)', text: '#6ee7b7', border: 'rgba(110,231,183,0.30)', glow: 'rgba(16,185,129,0.25)' },
  blue: { bg: 'rgba(59,130,246,0.10)', bgHover: 'rgba(59,130,246,0.18)', text: '#93c5fd', border: 'rgba(147,197,253,0.30)', glow: 'rgba(59,130,246,0.25)' },
  red: { bg: 'rgba(239,68,68,0.10)', bgHover: 'rgba(239,68,68,0.18)', text: '#fca5a5', border: 'rgba(252,165,165,0.30)', glow: 'rgba(239,68,68,0.25)' },
};

const GlassActionButton = ({ tone = 'cyan', icon, label, onClick, as = 'button', href, download, primary = false, disabled = false, testId }) => {
  const t = TONE_MAP[tone] || TONE_MAP.cyan;
  const Tag = as === 'a' ? 'a' : 'button';
  const props = as === 'a'
    ? { href, ...(download ? { download: true } : {}), 'data-testid': testId }
    : { onClick, disabled, type: 'button', 'data-testid': testId };
  return (
    <Tag
      {...props}
      title={label}
      aria-label={label}
      className={`relative aspect-square w-full flex flex-col items-center justify-center gap-1 rounded-2xl backdrop-blur-md transition group ${disabled ? 'opacity-30 pointer-events-none' : 'hover:-translate-y-0.5 active:translate-y-0'}`}
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        boxShadow: primary
          ? `inset 0 0 0 1px ${t.border}, 0 6px 18px -8px ${t.glow}, 0 0 0 1px ${t.border}`
          : `inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      <svg className="w-5 h-5 transition" fill="none" viewBox="0 0 24 24" stroke={t.text} strokeWidth={1.8}>
        {icon}
      </svg>
      <span className="text-[9px] font-semibold uppercase tracking-[0.15em]" style={{ color: t.text }}>
        {label}
      </span>
    </Tag>
  );
};

const ContractsManager = ({ isAdmin }) => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeSection, setActiveSection] = useState('members'); // 'members' | 'partners'
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractHtml, setContractHtml] = useState('');
  const [showModal, setShowModal] = useState(false);
  // Edit-before-generate flow
  const [editPreview, setEditPreview] = useState(null); // { member_id, member_name, plan, value, html, isPartner }
  const [editHtml, setEditHtml] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const loadContracts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contracts/all`);
      setContracts(res.data.contracts || []);
    } catch (err) {
      console.error('Erro ao carregar contratos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  const viewContract = async (contract) => {
    setSelectedContract(contract);
    try {
      if (contract.signed) {
        const res = await axios.get(`${API}/contracts/${contract.id}/signed`);
        setContractHtml(res.data.html_with_stamp || contract.html_content);
      } else {
        const res = await axios.get(`${API}/contracts/${contract.id}`);
        setContractHtml(res.data.html_content);
      }
      setShowModal(true);
    } catch (err) {
      console.error('Erro ao carregar contrato:', err);
    }
  };

  const generateShareLink = async (contract) => {
    try {
      const res = await axios.get(`${API}/contracts/${contract.id}/share`);
      const shareUrl = `${window.location.origin}/sign/${res.data.share_token}`;
      if (navigator.share) {
        navigator.share({ title: `Contrato WClub - ${contract.member_name || contract.partner_name}`, text: 'Assine seu contrato WClub', url: shareUrl });
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copiado!');
      }
    } catch (err) { alert('Erro ao gerar link'); }
  };

  const deleteContract = async (contract) => {
    if (!window.confirm(`Deletar contrato de ${contract.member_name || contract.partner_name}?`)) return;
    try {
      await axios.delete(`${API}/contracts/${contract.id}`);
      setContracts(prev => prev.filter(c => c.id !== contract.id));
      if (showModal && selectedContract?.id === contract.id) setShowModal(false);
    } catch (err) { alert('Erro ao deletar'); }
  };

  const regenerateContract = async (contract) => {
    // New flow: fetch preview HTML, open editor modal. Admin edits, then confirms to save.
    try {
      let res;
      if (contract.type === 'partner') {
        res = await axios.post(`${API}/partners/contracts/generate`, {
          partner_id: contract.partner_id,
          dry_run: true,
        });
      } else {
        res = await axios.post(`${API}/contracts/generate`, {
          member_id: contract.member_id,
          dry_run: true,
        });
      }
      const html = res.data?.html || '';
      if (!html) { alert('Nao foi possivel gerar a previa do contrato'); return; }
      setEditPreview({
        member_id: contract.member_id,
        partner_id: contract.partner_id,
        member_name: contract.member_name || contract.partner_name || '',
        plan: res.data?.plan,
        value: res.data?.value,
        isPartner: contract.type === 'partner',
      });
      setEditHtml(html);
    } catch (err) {
      alert('Erro ao gerar previa do contrato');
    }
  };

  const confirmEditedContract = async () => {
    if (!editPreview) return;
    setEditSaving(true);
    try {
      const url = editPreview.isPartner ? `${API}/partners/contracts/generate` : `${API}/contracts/generate`;
      const body = editPreview.isPartner
        ? { partner_id: editPreview.partner_id, html_content: editHtml }
        : { member_id: editPreview.member_id, html_content: editHtml };
      const res = await axios.post(url, body);
      if (res.data?.success) {
        await loadContracts();
        setEditPreview(null);
        setEditHtml('');
        alert('Contrato gerado com sucesso!');
      } else {
        alert('Erro ao salvar contrato');
      }
    } catch (err) {
      alert('Erro ao salvar contrato editado');
    } finally {
      setEditSaving(false);
    }
  };

  // Separate contracts by type
  const memberContracts = contracts.filter(c => c.type !== 'partner');
  const partnerContracts = contracts.filter(c => c.type === 'partner');
  const activeContracts = activeSection === 'members' ? memberContracts : partnerContracts;

  const filteredContracts = activeContracts.filter(c => {
    const name = c.member_name || c.partner_name || '';
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) || c.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || (filterStatus === 'signed' && c.signed) || (filterStatus === 'pending' && !c.signed);
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: activeContracts.length,
    signed: activeContracts.filter(c => c.signed).length,
    pending: activeContracts.filter(c => !c.signed).length
  };

  if (!isAdmin) {
    return <div className="flex items-center justify-center h-64 text-white/50">Acesso restrito a administradores</div>;
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-['Jost'] font-bold text-white mb-1">Contratos</h1>
        <p className="text-white/40 text-sm">Gerencie contratos de socios e parceiros</p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-5" data-testid="contracts-tabs">
        <button
          onClick={() => { setActiveSection('members'); setSearchTerm(''); setFilterStatus('all'); }}
          className={`flex-1 py-3 rounded-xl text-sm font-['Jost'] font-medium transition-all ${
            activeSection === 'members'
              ? 'bg-white/10 text-white border border-white/20'
              : 'bg-white/[0.03] text-white/40 border border-white/[0.06]'
          }`}
          data-testid="tab-members"
        >
          <div className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            Socios ({memberContracts.length})
          </div>
        </button>
        <button
          onClick={() => { setActiveSection('partners'); setSearchTerm(''); setFilterStatus('all'); }}
          className={`flex-1 py-3 rounded-xl text-sm font-['Jost'] font-medium transition-all ${
            activeSection === 'partners'
              ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
              : 'bg-white/[0.03] text-white/40 border border-white/[0.06]'
          }`}
          data-testid="tab-partners"
        >
          <div className="flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
            </svg>
            Parceiros ({partnerContracts.length})
          </div>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-white">{stats.total}</p>
          <p className="text-[10px] text-white/50">Total</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{stats.signed}</p>
          <p className="text-[10px] text-emerald-400/70">Assinados</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
          <p className="text-[10px] text-amber-400/70">Pendentes</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" placeholder="Buscar por nome ou ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 outline-none focus:border-white/20 text-sm" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white outline-none text-sm">
          <option value="all" className="bg-[#1a1a1a]">Todos</option>
          <option value="signed" className="bg-[#1a1a1a]">Assinados</option>
          <option value="pending" className="bg-[#1a1a1a]">Pendentes</option>
        </select>
      </div>

      {/* Contracts List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredContracts.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm">
          {searchTerm ? 'Nenhum contrato encontrado' : `Nenhum contrato de ${activeSection === 'members' ? 'socio' : 'parceiro'} ainda`}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredContracts.map((contract) => {
            const name = contract.member_name || contract.partner_name || 'N/A';
            const isPartner = contract.type === 'partner';
            return (
              <motion.div key={contract.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`relative rounded-2xl p-4 transition overflow-hidden ${isPartner ? 'border border-blue-500/15' : 'border border-white/[0.06]'}`}
                style={{
                  background: 'linear-gradient(135deg, rgba(20,25,35,0.7) 0%, rgba(8,11,18,0.85) 100%)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                }}>
                {/* Top row: avatar + name + meta */}
                <div className="flex items-start gap-3 mb-4 min-w-0">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                    contract.signed ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30' : 'bg-amber-500/15 text-amber-300 border border-amber-400/30'
                  }`}>
                    {contract.signed ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-semibold text-base sm:text-lg truncate leading-tight">{name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] sm:text-[11px]">
                      {isPartner ? (
                        <span className="px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-300 border border-blue-400/20 font-semibold tracking-wider">PARCEIRO</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-md font-semibold tracking-wider border ${
                          contract.plan === 'DIAMOND' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25' :
                          contract.plan === 'BLACK' ? 'bg-white/10 text-white/80 border-white/20' :
                          contract.plan === 'VIP' ? 'bg-purple-500/15 text-purple-300 border-purple-400/25' :
                          'bg-amber-500/15 text-amber-300 border-amber-400/25'
                        }`}>{contract.plan}</span>
                      )}
                      {contract.value != null && <span className="text-white/80 font-medium">R$ {Number(contract.value).toLocaleString('pt-BR')}</span>}
                      <span className="text-white/40">{new Date(contract.created_at).toLocaleDateString('pt-BR')}</span>
                      {contract.signed && (
                        <span className="px-2 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-400/25 rounded-md text-[9px] font-semibold tracking-wider">ASSINADO</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons row: uniform glass squares aligned */}
                <div className="grid grid-cols-5 gap-2">
                  <GlassActionButton
                    tone="cyan"
                    primary
                    onClick={() => regenerateContract(contract)}
                    label="Editar"
                    testId="contract-edit-btn"
                    icon={(
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    )}
                  />
                  <GlassActionButton
                    tone="violet"
                    onClick={() => generateShareLink(contract)}
                    label="Assinar"
                    disabled={contract.signed}
                    icon={(
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                    )}
                  />
                  <GlassActionButton
                    tone="emerald"
                    as="a"
                    href={`${API}/contracts/${contract.id}/pdf`}
                    download
                    label="PDF"
                    icon={(
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    )}
                  />
                  <GlassActionButton
                    tone="blue"
                    onClick={() => viewContract(contract)}
                    label="Ver"
                    icon={(<>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>)}
                  />
                  <GlassActionButton
                    tone="red"
                    onClick={() => deleteContract(contract)}
                    label="Excluir"
                    icon={(
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    )}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Contract Modal */}
      <AnimatePresence>
        {showModal && selectedContract && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[60] p-2 sm:p-4" onClick={() => setShowModal(false)}>
            <motion.div
              initial={{ scale: 0.95, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 30, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-2xl sm:rounded-3xl w-full max-w-4xl overflow-hidden flex flex-col"
              style={{
                background: 'linear-gradient(180deg, rgba(15,18,24,0.99) 0%, rgba(8,10,14,0.99) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                height: '92vh',
              }}>

              {/* STICKY HEADER */}
              <div className="flex-shrink-0 px-4 sm:px-5 py-3 border-b border-white/[0.06] flex items-center gap-3"
                   style={{ background: 'rgba(8,10,14,0.85)', backdropFilter: 'blur(12px)' }}>
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border ${
                  selectedContract.signed ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' :
                  selectedContract.type === 'partner' ? 'bg-blue-500/15 text-blue-300 border-blue-400/30' :
                  'bg-amber-500/15 text-amber-300 border-amber-400/30'
                }`}>
                  <span className="font-['Jost'] font-bold text-base">W</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold text-sm sm:text-base truncate leading-tight">
                    {selectedContract.member_name || selectedContract.partner_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded font-semibold tracking-wider border ${
                      selectedContract.type === 'partner' ? 'bg-blue-500/15 text-blue-300 border-blue-400/25' :
                      selectedContract.plan === 'DIAMOND' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25' :
                      selectedContract.plan === 'BLACK' ? 'bg-white/10 text-white/80 border-white/20' :
                      'bg-amber-500/15 text-amber-300 border-amber-400/25'
                    }`}>{selectedContract.type === 'partner' ? 'PARCEIRO' : selectedContract.plan}</span>
                    <span className={`px-1.5 py-0.5 rounded font-semibold tracking-wider border ${
                      selectedContract.signed
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25'
                        : 'bg-amber-500/15 text-amber-300 border-amber-400/25'
                    }`}>
                      {selectedContract.signed ? 'ASSINADO' : 'PENDENTE'}
                    </span>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition shrink-0">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              {/* STICKY ACTION BAR - glass squares aligned */}
              <div className="flex-shrink-0 px-4 sm:px-5 py-3 border-b border-white/[0.06] grid grid-cols-4 gap-2"
                   style={{ background: 'rgba(15,18,24,0.7)', backdropFilter: 'blur(12px)' }}>
                <GlassActionButton
                  tone="cyan"
                  primary
                  onClick={() => { setShowModal(false); regenerateContract(selectedContract); }}
                  label="Editar"
                  testId="contract-viewer-edit-btn"
                  icon={(
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                  )}
                />
                <GlassActionButton
                  tone="violet"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/contracts/${selectedContract.id}/pdf`);
                      const blob = await response.blob();
                      const file = new File([blob], `Contrato_WClub.pdf`, { type: 'application/pdf' });
                      if (navigator.share && navigator.canShare?.({ files: [file] })) {
                        await navigator.share({ title: 'Contrato WClub', files: [file] });
                      } else if (navigator.share) {
                        await navigator.share({ title: 'Contrato WClub', url: `${API}/contracts/${selectedContract.id}/pdf` });
                      } else { navigator.clipboard.writeText(`${API}/contracts/${selectedContract.id}/pdf`); alert('Link copiado!'); }
                    } catch (err) { if (err.name !== 'AbortError') console.error(err); }
                  }}
                  label="Enviar"
                  icon={(<path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />)}
                />
                <GlassActionButton
                  tone="emerald"
                  onClick={() => { const l = document.createElement('a'); l.href = `${API}/contracts/${selectedContract.id}/pdf`; l.download = 'Contrato.pdf'; l.click(); }}
                  label="PDF"
                  icon={(<path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />)}
                />
                <GlassActionButton
                  tone="blue"
                  onClick={() => { const w = window.open('','_blank'); w.document.write(contractHtml); w.document.close(); w.print(); }}
                  label="Imprimir"
                  icon={(<path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />)}
                />
              </div>

              {selectedContract.signed && selectedContract.digital_signature && (
                <div className="flex-shrink-0 bg-emerald-500/10 border-b border-emerald-500/20 px-4 py-2.5">
                  <div className="flex items-center gap-3 text-xs flex-wrap">
                    <span className="text-emerald-400 font-mono font-bold">{selectedContract.digital_signature.validation_code}</span>
                    <span className="text-emerald-400/70">{new Date(selectedContract.digital_signature.timestamp).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
              )}

              {/* SCROLLABLE BODY — only the contract scrolls, header stays put */}
              <div className="flex-1 min-h-0 overflow-hidden bg-neutral-200">
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"><style>html,body{margin:0;padding:0;width:100%;overflow-x:hidden;overflow-y:auto;-webkit-overflow-scrolling:touch}body>*{max-width:100%!important}img{max-width:100%;height:auto}</style></head><body>${contractHtml || ''}</body></html>`}
                  className="w-full h-full block bg-white"
                  style={{ border: 0 }}
                  title="Contrato"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit-before-generate Modal */}
      <AnimatePresence>
        {editPreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-end sm:items-center justify-center z-[80] p-2 sm:p-4"
            onClick={() => !editSaving && setEditPreview(null)}
            data-testid="contract-edit-modal"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0a0a0e] rounded-2xl sm:rounded-3xl border border-white/10 w-full sm:max-w-5xl max-h-[95vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-4 sm:px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-shrink-0">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 mb-1">Pré-visualização editável</div>
                  <h2 className="text-base sm:text-xl text-white font-['Jost'] font-semibold truncate">
                    Revisar contrato — {editPreview.member_name}
                  </h2>
                  {editPreview.plan && (
                    <p className="text-[11px] text-white/40 mt-0.5">
                      Plano <span className="text-white/70 font-medium">{editPreview.plan}</span>
                      {editPreview.value != null && (
                        <> · Valor <span className="text-white/70 font-medium">R$ {Number(editPreview.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></>
                      )}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => !editSaving && setEditPreview(null)}
                  disabled={editSaving}
                  className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40"
                  data-testid="contract-edit-close"
                  aria-label="Fechar"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Help bar */}
              <div className="px-4 sm:px-6 py-2 bg-cyan-500/5 border-b border-cyan-500/10 flex items-center gap-2 flex-shrink-0">
                <svg className="w-4 h-4 text-cyan-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <p className="text-[11px] sm:text-xs text-cyan-300/80 leading-snug">
                  Edite qualquer texto direto na pré-visualização abaixo. Depois clique em <strong className="text-cyan-300">Confirmar e gerar</strong> para salvar a versão definitiva.
                </p>
              </div>

              {/* Editable preview */}
              <div className="flex-1 overflow-auto bg-neutral-200 p-3 sm:p-6">
                <div
                  contentEditable={!editSaving}
                  suppressContentEditableWarning
                  spellCheck
                  className="bg-white text-black rounded-lg shadow-2xl mx-auto outline-none focus:ring-2 focus:ring-cyan-400/40"
                  style={{ maxWidth: '210mm', minHeight: '297mm', padding: '20mm 15mm' }}
                  data-testid="contract-edit-html"
                  dangerouslySetInnerHTML={{ __html: editHtml }}
                  onInput={(e) => setEditHtml(e.currentTarget.innerHTML)}
                />
              </div>

              {/* Footer actions */}
              <div className="px-4 sm:px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => setEditPreview(null)}
                  disabled={editSaving}
                  className="px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm uppercase tracking-widest text-white/70 hover:text-white border border-white/15 hover:border-white/30 transition disabled:opacity-40"
                  data-testid="contract-edit-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmEditedContract}
                  disabled={editSaving || !editHtml}
                  className="px-4 sm:px-6 py-2.5 rounded-full text-xs sm:text-sm uppercase tracking-widest font-semibold transition"
                  style={{
                    background: editSaving ? 'rgba(34,211,238,0.2)' : 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
                    color: '#000',
                  }}
                  data-testid="contract-edit-confirm"
                >
                  {editSaving ? 'Salvando…' : 'Confirmar e gerar'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractsManager;
